import { useMemo, useCallback } from 'react';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';

// ─── Zod Schema ────────────────────────────────────────────────────────────

export const documentSchema = z.object({
  titulo: z
    .string()
    .trim()
    .min(1, 'Título é obrigatório.')
    .max(200, 'Título deve ter no máximo 200 caracteres.'),
  familia: z.string().min(1, 'Família é obrigatória.'),
  tipo: z.string().min(1, 'Tipo é obrigatório.'),
  descricao: z
    .string()
    .trim()
    .max(2000, 'Descrição deve ter no máximo 2.000 caracteres.')
    .optional()
    .or(z.literal('')),
  arquivo: z
    .instanceof(File, { message: 'Arquivo é obrigatório.' })
    .refine(f => f.size <= 100 * 1024 * 1024, 'Arquivo deve ter no máximo 100 MB.')
    .refine(f => f.name.length <= 255, 'Nome do arquivo muito longo (máx. 255 caracteres).'),
});

export type DocumentFormData = z.infer<typeof documentSchema>;

// ─── Validation Errors ─────────────────────────────────────────────────────

export interface FieldErrors {
  titulo?: string;
  familia?: string;
  tipo?: string;
  descricao?: string;
  arquivo?: string;
  duplicata?: string;
}

// ─── Hook ──────────────────────────────────────────────────────────────────

interface DuplicateCheckResult {
  isDuplicate: boolean;
  existingDocTitle?: string;
}

export function useDocumentValidation(contratoId: string) {
  /**
   * Validate form data using zod schema.
   * Returns null if valid, or FieldErrors object if invalid.
   */
  const validateForm = useCallback(
    (data: {
      titulo: string;
      familia: string;
      tipo: string;
      descricao: string;
      arquivo: File | null;
    }): FieldErrors | null => {
      const result = documentSchema.safeParse({
        ...data,
        arquivo: data.arquivo ?? undefined,
      });

      if (result.success) return null;

      const errors: FieldErrors = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof FieldErrors;
        if (field && !errors[field]) {
          errors[field] = issue.message;
        }
      }
      return errors;
    },
    []
  );

  /**
   * Check if a document with same file name + tipo_documental already exists
   * for this contrato.
   */
  const checkDuplicate = useCallback(
    async (
      nomeArquivo: string,
      tipoDocumental: string
    ): Promise<DuplicateCheckResult> => {
      try {
        // Check emp_documento_versoes for matching file name in this contrato
        const { data, error } = await (supabase as any)
          .from('emp_documento_versoes')
          .select('documento_id, nome_arquivo, emp_documentos!inner(titulo, tipo_documental, contrato_id, ativo)')
          .eq('nome_arquivo', nomeArquivo)
          .eq('emp_documentos.contrato_id', contratoId)
          .eq('emp_documentos.tipo_documental', tipoDocumental)
          .eq('emp_documentos.ativo', true)
          .limit(1);

        if (error) {
          console.warn('Duplicate check failed:', error);
          return { isDuplicate: false };
        }

        if (data && data.length > 0) {
          return {
            isDuplicate: true,
            existingDocTitle: data[0]?.emp_documentos?.titulo || nomeArquivo,
          };
        }

        return { isDuplicate: false };
      } catch {
        return { isDuplicate: false };
      }
    },
    [contratoId]
  );

  return { validateForm, checkDuplicate };
}
